"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, Droplets, CircleQuestionMark as HelpCircle, PhoneCall, RefreshCw, Search, ShieldAlert, Wrench, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
const issues = [{
  id: "running",
  title: "Running & Non-Stop Filling",
  symptom: "Water constantly hissing or cycling on and off randomly",
  causes: "Worn flapper valve, broken fill valve, or improper float height",
  risk: "Wastes up to 200 gallons of water daily and drives up water bills",
  urgency: "Moderate Priority"
}, {
  id: "clogs",
  title: "Persistent Clogs & Backups",
  symptom: "Bowl fills to brim, drains slowly, or overflows onto bathroom floor",
  causes: "Foreign obstructions, heavy paper buildup, or main sewer line blockage",
  risk: "Immediate water damage to flooring and contaminated backup hazards",
  urgency: "Emergency"
}, {
  id: "leaks",
  title: "Base & Tank Leaks",
  symptom: "Water pooling around base bolts or dripping beneath the tank",
  causes: "Degraded wax ring seal, loose tank-to-bowl bolts, or hairline porcelain cracks",
  risk: "Subfloor rot, mold growth, and structural bathroom damage",
  urgency: "High Priority"
}, {
  id: "weak-flush",
  title: "Weak or Incomplete Flush",
  symptom: "Requires multiple flushes or fails to clear light waste from the bowl",
  causes: "Mineral buildup in rim jet holes, low tank water level, or loose chain",
  risk: "Repeated clogging and unsanitary bathroom conditions",
  urgency: "Standard"
}];
const diagnosticSteps = [{
  step: "01",
  title: "Visual & Moisture Inspection",
  desc: "We inspect tank bolts, supply connections, and the floor perimeter with moisture detection tools to catch hidden leaks."
}, {
  step: "02",
  title: "Tank Component Evaluation",
  desc: "Testing fill valves, flush canisters, flappers, and shut-off valves for wear, proper water line height, and sealing integrity."
}, {
  step: "03",
  title: "Drain Line & Wax Ring Check",
  desc: "Assessing bowl stability, seal airtightness, and verifying whether drain stoppages are local or rooted deeper in the branch pipe."
}, {
  step: "04",
  title: "Upfront Diagnostic Plan",
  desc: "We provide an exact quote explaining whether a targeted component repair, rebuild, or fixture replacement is the best solution."
}];
const faqItems = [{
  q: "How do I know if my toilet leak is coming from the wax ring?",
  a: "If water pools around the base of the toilet specifically after flushing, or if the toilet rocks slightly when you sit on it, the wax ring has likely failed and requires immediate re-setting."
}, {
  q: "Why does my toilet randomly make a running water sound for a few seconds?",
  a: "This is known as 'phantom flushing.' It happens when water slowly leaks out of the tank past a worn flapper into the bowl, causing the fill valve to kick in periodically to refill the tank."
}, {
  q: "When is it better to replace a toilet rather than repair it?",
  a: "If the porcelain has hairline cracks, if the toilet requires repeated costly repairs, or if it is an older high-water consumption model (3.5+ gallons per flush), upgrading to a modern efficient model is usually more cost-effective."
}];
export default function ToiletDiagnostics() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="toiletrepairdiagnosticoverview" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-14 md:mb-20">
          <Badge variant="secondary" className="mb-4 gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            <Search className="size-3.5 text-accent" />
            Fast Diagnostic Guide
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
            Toilet Repair Problems & On-Site Diagnostics
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans">
            From emergency overflows to silent running leaks that spike utility bills, Jonesboro Emergency Plumber diagnoses and resolves toilet malfunctions with precision.
          </p>
        </div>

        {/* 4 Common Problems Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16 md:mb-24">
          {issues.map((item, i) => <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.1
        }}>
              <Card className="h-full border border-border shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="inline-flex items-center justify-center p-2 rounded-lg bg-secondary text-primary">
                      {item.id === "running" && <RefreshCw className="size-5" />}
                      {item.id === "clogs" && <ShieldAlert className="size-5 text-destructive" />}
                      {item.id === "leaks" && <Droplets className="size-5 text-chart-3" />}
                      {item.id === "weak-flush" && <Wrench className="size-5 text-accent-foreground" />}
                    </span>
                    <Badge variant={item.urgency === "Emergency" ? "destructive" : item.urgency === "High Priority" ? "default" : "secondary"} className="font-medium">
                      {item.urgency}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-default font-bold text-card-foreground">
                    {item.title}
                  </CardTitle>
                  <CardDescription className="text-sm font-sans text-muted-foreground mt-1">
                    <strong className="text-foreground font-medium">Symptom:</strong> {item.symptom}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 pt-2 text-sm font-sans">
                  <div className="p-3 rounded-md bg-muted/60 border border-border/50">
                    <p className="text-muted-foreground">
                      <strong className="text-foreground font-medium">Typical Cause:</strong> {item.causes}
                    </p>
                  </div>
                  <div className="flex items-start gap-2 text-muted-foreground">
                    <AlertTriangle className="size-4 shrink-0 text-accent mt-0.5" />
                    <span><strong className="text-foreground font-medium">Risk:</strong> {item.risk}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Diagnostic Process Section */}
        <div className="rounded-2xl bg-card border border-border p-6 sm:p-10 lg:p-12 mb-16 md:mb-24 shadow-sm">
          <div className="max-w-2xl mb-10">
            <Badge variant="outline" className="mb-3 text-xs font-semibold uppercase tracking-wider">
              Methodical Troubleshooting
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-card-foreground tracking-tight">
              Our 4-Step Toilet Diagnostic Process
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground font-sans">
              We do not guess at plumbing issues. Our Jonesboro plumbers follow an exact checklist to isolate the core problem quickly.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {diagnosticSteps.map((step, i) => <div key={step.step} data-index={i} className="relative flex flex-col p-5 rounded-xl bg-background border border-border">
                <div className="text-2xl font-mono font-bold text-primary mb-3">
                  {step.step}
                </div>
                <h4 className="text-base font-default font-semibold text-foreground mb-2">
                  {step.title}
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground font-sans leading-relaxed">
                  {step.desc}
                </p>
              </div>)}
          </div>

          <div className="mt-10 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="size-5 text-chart-4 shrink-0" />
              <span className="text-sm font-sans font-medium text-foreground">
                All repairs backed by licensed, insured emergency plumbing professionals.
              </span>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                <Link to="/services/">
                  View All Services
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Diagnostic FAQs & Emergency CTA */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-4">
              <HelpCircle className="size-5 text-primary" />
              <h3 className="text-xl sm:text-2xl font-default font-bold text-foreground">
                Frequently Asked Diagnostic Questions
              </h3>
            </div>
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((faq, i) => <AccordionItem key={faq.q} value={`item-${i}`} data-index={i}>
                  <AccordionTrigger className="text-left font-sans font-medium text-foreground hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground font-sans text-sm leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {/* Urgent Dispatch CTA Card */}
          <div className="lg:col-span-5">
            <Card className="bg-primary text-primary-foreground border-transparent shadow-md">
              <CardHeader className="pb-3">
                <Badge variant="secondary" className="w-fit bg-accent text-accent-foreground font-semibold">
                  24/7 Emergency Dispatch
                </Badge>
                <CardTitle className="text-2xl font-default font-bold text-primary-foreground mt-2">
                  Toilet Backing Up or Leaking Now?
                </CardTitle>
                <CardDescription className="text-primary-foreground/80 font-sans text-sm">
                  Do not wait for water damage to spread into your subfloors or ceiling below. Contact our local Jonesboro technicians immediately.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-2">
                <div className="p-4 rounded-lg bg-primary-foreground/10 border border-primary-foreground/15 text-xs sm:text-sm font-sans space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-accent" />
                    <span>Fast arrival across Jonesboro & Clayton County</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-accent" />
                    <span>Upfront quotes with no hidden overtime fees</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button asChild size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-xs">
                    <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                      <PhoneCall className="size-4" />
                      Call for Emergency Repair
                    </a>
                  </Button>
                </div>
                <div className="text-center">
                  <Button asChild variant="link" size="sm" className="text-primary-foreground/90 hover:text-primary-foreground text-xs">
                    <Link to="/contact/">Or request diagnostic inspection online</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}